import React, { useState, useEffect } from "react";
import { Card, Button, Image, Spinner, Modal, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../../config";
import companyIcon from "../../../images/office.png";
import { logoutonexpire } from "../../../store/actions/AuthActions";
import moment from "moment";
import Select from "react-select";
import JobCard from "./JobCard";
import { categoryImages } from "../../../utils/helper";

const CompanyView = () => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const collegeId = useSelector((state) => state.college_id.college_id);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [profileImageError, setProfileImageError] = useState(false);
  const [copyMessageEmail, setCopyMessageEmail] = useState(false);
  const [companyMessage, setCompanyMessage] = useState(null);
  const [companyJobs, setCompanyJobs] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");

  // New state variables for the form
  const [emailTitle, setEmailTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [passoutYear, setPassoutYear] = useState(null);
  const [fileError, setFileError] = useState("");

  // Handle image error
  const handleImageError = () => {
    setProfileImageError(true);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("DD-MM-YYYY");
  };

  // Mask email for privacy
  const maskEmail = (email) => {
    if (!email) return "N/A";
    const [username, domain] = email.split("@");
    const maskedUsername = username.charAt(0) + "*".repeat(username.length - 1);
    return `${maskedUsername}@${domain}`;
  };

  // Mask phone number for privacy
  const maskPhone = (phone) => {
    if (!phone) return "N/A";
    return "*".repeat(phone.length - 2) + phone.slice(-2);
  };

  // Fetch company profile data
  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/api/freshment_portal_apis/CompanyConnect/get_company_profile?company_id=${companyId}`,
      );
      console.log(response.data.data);
      setCompanyData(response.data.data);
      setLoading(false);
      // fetchCompanyJobs();
      setCompanyJobs(response.data?.data?.job_list ?? []);
    } catch (error) {
      console.error("Error fetching company profile:", error);
      setCompanyMessage(
        "Failed to fetch company profile. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Check file size (max 2MB)
    if (file && file.size > 2 * 1024 * 1024) {
      setFileError("File size should be less than 2MB");
      setSelectedFile(null);
      return;
    }

    setFileError("");
    setSelectedFile(file);
  };

  // Submit campus drive request
  const submitRequest = async () => {
    // Validate form inputs
    if (!emailTitle.trim()) {
      toast.error("Please enter a title for your request");
      return;
    }

    if (!requestMessage.trim()) {
      toast.error("Please enter a message for your campus drive request");
      return;
    }

    if (!selectedFile) {
      toast.error("Please upload a college brochure");
      return;
    }

    if (passoutYear === null) {
      toast.error("Please select passout year");
      return;
    }

    try {
      setLoading(true);
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;

      const formData = new FormData();
      formData.append("college_id", collegeId);
      formData.append("company_id", companyId);
      formData.append("email_title", emailTitle);
      formData.append("notification_message", requestMessage);
      formData.append("passout_year", passoutYear.value);

      if (selectedFile instanceof File) {
        const files = new FormData();
        files.append("image", selectedFile);
        files.append("college_id", collegeId);
        const PdfUpload = await axios.post(
          `${API_URL}/api/freshment_portal_apis/CompanyConnect/upload_college_brouchre`,
          files,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${authToken}`,
            },
          },
        );
        if (PdfUpload.data?.data?.college_brochure) {
          formData.append(
            "college_pdf_url",
            PdfUpload.data?.data?.college_brochure_file,
          );
        }
      }

      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/CompanyConnect/connect_company_by_colleges`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.data?.success) {
        setShowRequestModal(false);
        resetForm();
        toast.success(
          response.data?.message || "Campus drive request sent successfully",
        );

        // Update company data to show request status if needed
        setCompanyData((prev) => ({
          ...prev,
          request_status: "Request Sent",
        }));
      } else {
        throw new Error(response.data.message || "Failed to send request");
      }
    } catch (error) {
      console.error("Error sending campus drive request:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to send request. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset form fields after submission or cancel
  const resetForm = () => {
    setEmailTitle("");
    setRequestMessage("");
    setSelectedFile(null);
    setPassoutYear(null);
    setFileError("");
  };

  // Close modal and reset form
  const handleCloseModal = () => {
    setShowRequestModal(false);
    resetForm();
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCompanyProfile();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [companyId]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading company profile...</p>
      </div>
    );
  }

  if (companyMessage) {
    return <div className="alert alert-danger">{companyMessage}</div>;
  }

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          <Card className="h-auto">
            <Card.Body>
              {/* Header Section */}
              <div className="d-flex flex-column flex-md-row align-items-md-center border-bottom pb-4 mb-4 justify-content-between">
                <div className="d-flex flex-column flex-md-row align-items-md-center col-12 col-md-8 mb-3 mb-md-0">
                  <div className="text-center text-md-start mb-3 mb-md-0">
                    <Image
                      src={
                        categoryImages[
                          companyData?.job_list[0]?.job_category_id
                        ] || companyIcon
                      }
                      alt={companyData?.company_name}
                      className="me-md-3 p-1"
                      rounded
                      width={90}
                      height={90}
                      onError={handleImageError}
                      style={{ objectFit: "cover" }}
                    />
                  </div>
                  <div className="text-center text-md-start">
                    <h3 className="mb-1">{companyData?.company_name}</h3>

                    {companyData?.job_list?.[0]?.job_location && (
                      <p>
                        <i className="fas fa-map-marker-alt me-2"></i>
                        {companyData?.job_list?.[0]?.job_location ||
                          "Address not available"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 mt-md-0">
                  <div className="d-flex justify-content-center justify-content-md-end align-items-start">
                    <Button
                      variant="primary"
                      onClick={() => setShowRequestModal(true)}
                      className="btn-sm w-100 w-md-auto"
                    >
                      <i className="fas fa-paper-plane me-2 d-none d-sm-inline-block"></i>
                      Request For Campus Drive
                    </Button>
                  </div>
                </div>
              </div>

              {/* Company Information */}
              <div className="row">
                {/* Basic Information */}
                <div className="col-12 col-xl-7 mb-4 mb-xl-0">
                  {companyData?.remark && (
                    <>
                      <div className="mb-3 px-2">
                        <h4 className="mb-0">
                          College Requested Status Remark
                        </h4>
                      </div>
                      <div
                        role="alert"
                        className="alert alert-success fade show"
                      >
                        <span className="text-secondary">
                          {companyData?.remark}
                        </span>
                      </div>
                    </>
                  )}

                  {/* About Company */}
                  {companyData?.about_me && (
                    <div className="card h-auto jobs2 mb-4">
                      <div className="card-header">
                        <h4 className="mb-0">About Company</h4>
                      </div>
                      <div className="card-body">
                        <p>
                          {companyData?.about_me || "No information available."}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Jobs Posted */}
                  <div className="card h-auto jobs2 mb-4">
                    <div className="card-header border-bottom border-bottom-1 d-flex justify-content-between align-items-center py-3">
                      <h4 className="mb-0">Jobs Posted</h4>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                        {companyJobs.length > 0 ? (
                          companyJobs.map((job, index) => (
                            <JobCard job={job} key={index} />
                          ))
                        ) : (
                          <div className="w-100 text-center py-4">
                            <p>No jobs posted by this company yet.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="col-12 col-xl-5">
                  <div className="card h-auto mb-4 jobs2">
                    <div className="card-header">
                      <h4 className="mb-0">Contact Information</h4>
                    </div>
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3">
                        <div className="d-flex justify-content-between w-100 align-items-center">
                          <span className="text-break">
                            <i className="fas fa-envelope me-2"></i>
                            {companyData?.company_email ?? "N/A"}
                          </span>
                        </div>
                      </div>
                      {companyData?.company_contact_no && (
                        <p>
                          <i className="fas fa-phone me-2"></i>
                          {maskPhone(companyData?.company_contact_no)}
                        </p>
                      )}
                      {companyData?.company_address && (
                        <p className="mb-0 text-black">
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {companyData?.company_address ||
                            "Location not available"}
                        </p>
                      )}
                      {companyData?.website && (
                        <p className="text-break">
                          <i className="fas fa-globe me-2"></i>
                          <a
                            href={companyData.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {companyData.website}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <Modal
        show={showRequestModal}
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header>
          <Modal.Title>
            Request Campus Drive - {companyData?.company_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={emailTitle}
                onChange={(e) => setEmailTitle(e.target.value)}
                placeholder="Enter request title"
              />
            </Form.Group>

            <Form.Group id="multiselect" className="mb-3">
              <Form.Label>Select Passout Year</Form.Label>
              <Select
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    padding: "10px",
                    borderRadius: "1rem",
                  }),
                  menuList: (baseStyles) => ({
                    ...baseStyles,
                    padding: "10px",
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: "1000",
                    borderRadius: "1rem",
                  }),
                  multiValue: (baseStyles) => ({
                    ...baseStyles,
                    padding: "5px 10px",
                    borderRadius: "1rem",
                  }),
                }}
                value={passoutYear}
                onChange={(option) => setPassoutYear(option)}
                options={[
                  { value: "2023", label: "2023" },
                  { value: "2024", label: "2024" },
                  { value: "2025", label: "2025" },
                  { value: "2026", label: "2026" },
                  { value: "2027", label: "2027" },
                ]}
                placeholder="Select Passout Year"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Upload College Brochure (Max 2MB)</Form.Label>
              <Form.Control
                type="file"
                className="form-control pt-3"
                accept=".pdf"
                onChange={handleFileChange}
              />
              {fileError && (
                <Form.Text className="text-danger">{fileError}</Form.Text>
              )}
              {/* {selectedFile && (
                <Form.Text className="text-success">
                  {selectedFile.name} selected
                </Form.Text>
              )} */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Please provide details about your campus drive request..."
                style={{ height: "120px", border: "1px solid #cccccc" }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="" className="btn btn-sm btn-danger" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="" className="btn btn-sm btn-primary" onClick={submitRequest} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> Sending...
              </>
            ) : (
              "Send Request"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CompanyView;
